import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Divider,
  Empty,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Skeleton,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LeftOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  RightOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import cloneDeep from "lodash/cloneDeep";
import DOMPurify from "dompurify";
import { allApiAction } from "../../Redux/common/action";
import "./OrderDetails.css";
import FunctionModal from "./AddFunctionModal";
import { URL } from "../../Redux/common/url";

/*
 * Convert HTML descriptions (e.g. "<p>Capacity:500</p>")
 * into clean plain text so raw tags never show in the UI.
 */
const getPlainText = (html = "") => {
  if (!html) return "";

  const sanitizedHtml = DOMPurify.sanitize(String(html));
  const doc = new DOMParser().parseFromString(sanitizedHtml, "text/html");

  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [remark, setRemark] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [orderDetails, setOrderDetails] = useState({});
  const [functionModal, setFunctionModal] = useState(false);
  const [manipulatedData, setManipulatedData] = useState({});

  const { loading, order_details } = useSelector((state) => ({
    order_details: state?.allApi?.order_details || {},
    loading: state?.allApi?.loading,
  }));

  /* ================= FETCH ================= */

  useEffect(() => {
    if (orderId) {
      dispatch(allApiAction.getOrderDetail({ order_id: orderId })).catch(() =>
        message.error("Failed to fetch order details")
      );
    }
  }, [orderId, dispatch]);

  const derivedOrderDetails = useMemo(() => {
    if (order_details?.id) {
      return {
        ...order_details,
        remark: order_details.remark || "",
        discount: order_details.discount || 0,
      };
    }
    return {};
  }, [order_details]);

  useEffect(() => {
    setOrderDetails(derivedOrderDetails);
    setManipulatedData(cloneDeep(derivedOrderDetails));
    setRemark(derivedOrderDetails.remark || "");
    setDiscount(derivedOrderDetails.discount || 0);
  }, [derivedOrderDetails]);

  /* ================= HELPERS ================= */

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount) || 0;

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount);
  };

  /*
   * Calculate total.
   *
   * total_function_amount already contains the selected
   * essential amounts according to the API response.
   */
  const calculateTotalAmount = (data) => {
    let total = 0;

    data?.functions?.forEach((func) => {
      total += Number(func?.total_function_amount) || 0;
    });

    return total;
  };

  const updateFinalAmount = () => {
    const total = calculateTotalAmount(manipulatedData);

    return Math.max(0, total - Number(discount || 0));
  };

  const counts = useMemo(() => {
    const functions = manipulatedData?.functions || [];
    let essentials = 0;
    let items = 0;

    functions.forEach((fn) => {
      (fn?.essentials || []).forEach((ess) => {
        essentials += 1;
        items += (ess?.essential_types || []).length;
      });
    });

    return { functions: functions.length, essentials, items };
  }, [manipulatedData]);

  const getImageUrl = (path) => {
    if (!path) return "/placeholder.svg";

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const baseUrl = URL?.API_BASE_URL || "";

    return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  };

  const statusColors = {
    pending: "orange",
    paid: "green",
    success: "green",
    completed: "green",
    failed: "red",
    cancelled: "red",
  };

  /* ================= MODAL ================= */

  const handleFunctionModal = () => {
    setFunctionModal(true);

    dispatch(
      allApiAction.getFunctions({
        page: 1,
        occasion_id: orderDetails?.occasion_id,
      })
    );
  };

  /* ================= REMOVE HANDLERS ================= */

  const handleRemoveEssentialType = (
    functionIndex,
    essentialIndex,
    essentialTypeIndex
  ) => {
    const newData = cloneDeep(manipulatedData);

    const func = newData.functions?.[functionIndex];
    if (!func) return;

    const essential = func.essentials?.[essentialIndex];
    if (!essential) return;

    const removedAmount =
      Number(essential?.essential_types?.[essentialTypeIndex]?.amount) || 0;

    essential.essential_types.splice(essentialTypeIndex, 1);

    essential.total_essential_amount =
      Number(essential.total_essential_amount || 0) - removedAmount;

    func.total_function_amount =
      Number(func.total_function_amount || 0) - removedAmount;

    if (essential.essential_types.length === 0) {
      func.essentials.splice(essentialIndex, 1);
    }

    if (func.essentials.length === 0 && !func.venue) {
      newData.functions.splice(functionIndex, 1);
    }

    newData.total_amount = calculateTotalAmount(newData);
    newData.final_amount = Math.max(
      0,
      calculateTotalAmount(newData) - Number(discount || 0)
    );

    setManipulatedData(newData);
  };

  const handleRemoveEssential = (functionIndex, essentialIndex) => {
    const newData = cloneDeep(manipulatedData);

    const func = newData.functions?.[functionIndex];
    if (!func) return;

    const removedAmount =
      Number(func?.essentials?.[essentialIndex]?.total_essential_amount) || 0;

    func.essentials.splice(essentialIndex, 1);

    func.total_function_amount =
      Number(func.total_function_amount || 0) - removedAmount;

    if (func.essentials.length === 0 && !func.venue) {
      newData.functions.splice(functionIndex, 1);
    }

    newData.total_amount = calculateTotalAmount(newData);
    newData.final_amount = Math.max(
      0,
      calculateTotalAmount(newData) - Number(discount || 0)
    );

    setManipulatedData(newData);
  };

  const handleRemoveFunction = (functionIndex) => {
    const newData = cloneDeep(manipulatedData);

    newData.functions.splice(functionIndex, 1);

    newData.total_amount = calculateTotalAmount(newData);
    newData.final_amount = Math.max(
      0,
      calculateTotalAmount(newData) - Number(discount || 0)
    );

    setManipulatedData(newData);
  };

  const handleRemoveVenue = (functionIndex) => {
    const newData = cloneDeep(manipulatedData);

    if (!newData.functions?.[functionIndex]) return;

    newData.functions[functionIndex].venue = null;

    newData.total_amount = calculateTotalAmount(newData);
    newData.final_amount = Math.max(
      0,
      calculateTotalAmount(newData) - Number(discount || 0)
    );

    setManipulatedData(newData);
  };

  /* ================= EDIT HANDLERS ================= */

  const handleDiscountChange = (value) => {
    const newDiscount = Math.max(0, Number(value) || 0);

    setDiscount(newDiscount);

    const newData = cloneDeep(manipulatedData);

    newData.discount = newDiscount;
    newData.total_amount = calculateTotalAmount(newData);
    newData.final_amount = Math.max(
      0,
      calculateTotalAmount(newData) - newDiscount
    );

    setManipulatedData(newData);
  };

  const handleGuestNoChange = (functionIndex, value) => {
    const newData = cloneDeep(manipulatedData);

    newData.functions[functionIndex].guest_no = Number(value) || 0;

    setManipulatedData(newData);
  };

  const handleDateChange = (functionIndex, value) => {
    const newData = cloneDeep(manipulatedData);

    newData.functions[functionIndex].date = value;

    setManipulatedData(newData);
  };

  /* ================= SUBMIT ================= */

  const prepareSubmitData = () => {
    return {
      order_id: parseInt(orderId),
      occasion_id: orderDetails?.occasion_id,
      total_amount: calculateTotalAmount(manipulatedData),
      discount: Number(discount) || 0,
      date: orderDetails?.date
        ? moment(orderDetails.date).format("YYYY-MM-DD")
        : "",
      remark: remark,
      functions:
        manipulatedData?.functions?.map((func) => ({
          function_id: func?.function?.id || func?.function_id,
          total_function_amount: Number(func?.total_function_amount) || 0,
          venue_id: func?.venue?.id || null,
          venue_amount: Number(func?.venue?.amount) || 0,
          guest_no: Number(func?.guest_no) || 0,
          date: func?.date || "",
          essentials:
            func?.essentials?.map((essential) => ({
              essential_id:
                essential?.essential?.id || essential?.essential_id,
              essential_types:
                essential?.essential_types?.map((type) => ({
                  essential_type_id:
                    type?.essential_type?.id || type?.essential_type_id,
                })) || [],
            })) || [],
        })) || [],
    };
  };

  const handleSubmit = () => {
    const submitData = prepareSubmitData();

    dispatch(allApiAction.updateOrder(submitData))
      .then(() => {
        message.success("Order updated successfully");
        setIsEditing(false);

        dispatch(allApiAction.getOrderDetail({ order_id: orderId }));
      })
      .catch(() => {
        message.error("Failed to update order");
      });
  };

  const handleCancel = () => {
    setManipulatedData(cloneDeep(orderDetails));
    setRemark(orderDetails?.remark || "");
    setDiscount(orderDetails?.discount || 0);
    setIsEditing(false);
  };

  const handleEdit = () => setIsEditing(true);

  /* ================= INVOICE ================= */

  const generateInvoice = () => {
    const totalAmount = calculateTotalAmount(manipulatedData);
    const finalAmount = updateFinalAmount();

    const invoiceDiv = document.createElement("div");

    invoiceDiv.id = "print-invoice";

    invoiceDiv.innerHTML = `
      <style>
        #print-invoice {
          font-family: Arial, sans-serif;
          padding: 30px;
          color: #333;
          background: #fff;
        }

        #print-invoice .invoice-box {
          max-width: 900px;
          margin: auto;
          border: 1px solid #ddd;
          padding: 30px;
        }

        #print-invoice .header {
          display: flex;
          justify-content: space-between;
        }

        #print-invoice h1 {
          color: #1559a2;
        }

        #print-invoice table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }

        #print-invoice table,
        #print-invoice th,
        #print-invoice td {
          border: 1px solid #ddd;
        }

        #print-invoice th {
          background: #1559a2;
          color: white;
          padding: 10px;
        }

        #print-invoice td {
          padding: 10px;
          vertical-align: top;
        }

        #print-invoice .sub-item {
          margin-left: 30px;
          font-size: 13px;
          color: #666;
        }

        #print-invoice .essential {
          margin-top: 10px;
          padding-left: 15px;
        }

        #print-invoice .amount-section {
          margin-top: 30px;
          text-align: right;
          border-top: 2px solid #ddd;
          padding-top: 15px;
        }

        #print-invoice .final {
          font-size: 22px;
          color: #1559a2;
          font-weight: bold;
        }

        @media print {
          body * {
            visibility: hidden;
          }

          #print-invoice,
          #print-invoice * {
            visibility: visible;
          }

          #print-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      </style>

      <div class="invoice-box">

        <div class="header">
          <div>
            <h1>Eventm Fiesta</h1>
          </div>

          <div>
            <h2>INVOICE</h2>

            <p>
              Invoice No: #${orderDetails?.id || "-"}
              <br/>
              Date:
              ${
                orderDetails?.date
                  ? moment(orderDetails.date).format("DD MMM YYYY")
                  : "-"
              }
            </p>
          </div>
        </div>

        <hr/>

        <h3>Customer Details</h3>

        <p>
          <b>Name:</b>
          ${orderDetails?.user?.name || "-"}
        </p>

        <p>
          <b>Mobile:</b>
          ${orderDetails?.user?.mobile || "-"}
        </p>

        <p>
          <b>Email:</b>
          ${orderDetails?.user?.email || "-"}
        </p>

        <h3>Order Details</h3>

        <p>
          <b>Occasion:</b>
          ${orderDetails?.occasion?.title || "-"}
        </p>

        <h3>Services</h3>

        <table>
          <thead>
            <tr>
              <th>Service Details</th>
              <th>Guests</th>
              <th>Date</th>
              <th>Amount</th>
            </tr>
          </thead>

          <tbody>

            ${
              manipulatedData?.functions
                ?.map(
                  (func, index) => `
                    <tr>

                      <td>

                        <b>
                          ${index + 1}. ${func?.function?.name || "N/A"}
                        </b>

                        <br/>

                        Function Amount:
                        <b>
                          ${formatCurrency(
                            func?.total_function_amount || 0
                          )}
                        </b>

                        ${
                          func?.venue
                            ? `
                              <div class="essential">
                                <b>Venue:</b>
                                ${func.venue.name || "-"}

                                <br/>

                                Venue Amount:
                                <b>
                                  ${formatCurrency(
                                    func.venue.amount || 0
                                  )}
                                </b>
                              </div>
                            `
                            : ""
                        }

                        ${
                          func?.essentials
                            ?.map(
                              (essential) => `
                                <div class="essential">

                                  <b>Essential:</b>
                                  ${
                                    essential?.essential?.name ||
                                    "N/A"
                                  }

                                  <br/>

                                  Essential Total:
                                  <b>
                                    ${formatCurrency(
                                      essential?.total_essential_amount ||
                                        0
                                    )}
                                  </b>

                                  ${
                                    essential?.essential_types
                                      ?.map(
                                        (type) => `
                                          <div class="sub-item">
                                            -
                                            ${
                                              type?.essential_type
                                                ?.name || "N/A"
                                            }
                                            :
                                            <b>
                                              ${formatCurrency(
                                                type?.amount || 0
                                              )}
                                            </b>
                                          </div>
                                        `
                                      )
                                      .join("") || ""
                                  }

                                </div>
                              `
                            )
                            .join("") || ""
                        }

                      </td>

                      <td align="center">
                        ${func?.guest_no || 0}
                      </td>

                      <td align="center">
                        ${
                          func?.date
                            ? moment(func.date).format("DD MMM YYYY")
                            : "-"
                        }
                      </td>

                      <td align="right">
                        <b>
                          ${formatCurrency(func?.total_function_amount || 0)}
                        </b>
                      </td>

                    </tr>
                  `
                )
                .join("") || ""
            }

          </tbody>
        </table>

        <div class="amount-section">

          <p>
            <b>Sub Total:</b>
            ${formatCurrency(totalAmount)}
          </p>

          <p>
            <b>Discount:</b>
            ${formatCurrency(discount)}
          </p>

          <p class="final">
            Final Amount:
            ${formatCurrency(finalAmount)}
          </p>

        </div>

        <h4>Remark</h4>

        <p>
          ${remark || "-"}
        </p>

        <br/>

        <center>
          Thank You
        </center>

      </div>
    `;

    document.body.appendChild(invoiceDiv);

    window.print();

    document.body.removeChild(invoiceDiv);
  };

  /* ================= STATES ================= */

  if (loading) {
    return (
      <div className="od-page">
        <div className="od-container">
          <Skeleton active avatar paragraph={{ rows: 10 }} />
        </div>
      </div>
    );
  }

  if (!order_details?.id) {
    return (
      <div className="od-page">
        <div className="od-container">
          <div className="od-card od-empty">
            <Empty description="No Order Details Found" />
            <p className="od-empty-text">
              The order with ID #{orderId} does not exist or could not be
              loaded.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  /* ================= RENDER ================= */

  return (
    <div className={`od-page ${isEditing ? "od-editing" : ""}`}>
      <div className="od-container">
        {/* ===== PAGE HEADER ===== */}
        <header className="od-header">
          <div className="od-header-left">
            <Button
              className="od-back-btn"
              type="text"
              icon={<LeftOutlined />}
              onClick={() => navigate(-1)}
            />
            <div className="od-heading">
              <div className="od-title-row">
                <h1 className="od-title">Order Details</h1>
                <Tag className="od-order-tag">#{orderDetails?.id}</Tag>
                <Tag
                  color={
                    statusColors[orderDetails?.payment_status] || "blue"
                  }
                >
                  {(orderDetails?.payment_status || "Unknown").toUpperCase()}
                </Tag>
              </div>
              <div className="od-subtitle">
                {[
                  orderDetails?.occasion?.title,
                  orderDetails?.date
                    ? moment(orderDetails.date).format("DD MMM, YYYY")
                    : null,
                ]
                  .filter(Boolean)
                  .join("  •  ") || "—"}
              </div>
            </div>
          </div>

          <div className="od-header-actions">
            {isEditing ? (
              <>
                <Button icon={<CloseOutlined />} onClick={handleCancel}>
                  Discard
                </Button>
                <Button
                  type="primary"
                  className="od-btn-primary"
                  icon={<CheckOutlined />}
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={loading}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {orderDetails?.functions?.length > 0 && (
                  <Button icon={<EditOutlined />} onClick={handleEdit}>
                    Edit
                  </Button>
                )}
                <Button
                  icon={<PlusOutlined />}
                  onClick={handleFunctionModal}
                >
                  Add Function
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={generateInvoice}
                >
                  Invoice
                </Button>
              </>
            )}
          </div>
        </header>

        {/* ===== BODY ===== */}
        <div className="od-body">
          <div className="od-main">
            {(manipulatedData?.functions?.length || 0) === 0 ? (
              <div className="od-card od-empty">
                <Empty description="No functions in this order yet" />
                <Button
                  type="primary"
                  className="od-btn-primary"
                  icon={<PlusOutlined />}
                  onClick={handleFunctionModal}
                >
                  Add Function
                </Button>
              </div>
            ) : (
              manipulatedData.functions.map((fn, fi) => (
                <section
                  className="od-card od-function"
                  key={fn?.id || fn?.function_id || `fn-${fi}`}
                >
                  {/* Function header */}
                  <div className="od-function-head">
                    {fn?.function?.image ? (
                      <div className="od-function-icon od-function-icon-img">
                        <img
                          src={getImageUrl(fn.function.image)}
                          alt={fn?.function?.name}
                        />
                      </div>
                    ) : (
                      <div className="od-function-icon">
                        {(fn?.function?.name || "F").charAt(0).toUpperCase()}
                      </div>
                    )}

                    <div className="od-function-info">
                      <h3 className="od-function-name">
                        {fn?.function?.name || "Function"}
                      </h3>
                      <div className="od-chips">
                        <span className="od-chip">
                          <TeamOutlined /> {fn?.guest_no || 0} Guests
                        </span>
                        <span className="od-chip">
                          <CalendarOutlined />{" "}
                          {fn?.date
                            ? moment(fn.date).format("DD MMM, YYYY")
                            : "No date set"}
                        </span>
                      </div>
                    </div>

                    <div className="od-function-price">
                      <span className="od-price-label">Function Total</span>
                      <span className="od-price-value">
                        {formatCurrency(fn?.total_function_amount || 0)}
                      </span>
                    </div>

                    {isEditing && (
                      <Popconfirm
                        title="Remove this function?"
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleRemoveFunction(fi)}
                      >
                        <Tooltip title="Remove Function">
                          <Button
                            className="od-icon-btn"
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                          />
                        </Tooltip>
                      </Popconfirm>
                    )}
                  </div>

                  {/* Function body */}
                  <div className="od-function-body">
                    {isEditing ? (
                      <div className="od-edit-row">
                        <div className="od-field">
                          <label>Guests</label>
                          <InputNumber
                            min={0}
                            style={{ width: 160 }}
                            value={fn?.guest_no ?? 0}
                            onChange={(v) => handleGuestNoChange(fi, v)}
                          />
                        </div>
                        <div className="od-field">
                          <label>Function Date</label>
                          <Input
                            type="date"
                            style={{ width: 180 }}
                            value={fn?.date || ""}
                            onChange={(e) =>
                              handleDateChange(fi, e.target.value)
                            }
                          />
                        </div>
                        <Button
                          className="od-bookings-btn"
                          onClick={() =>
                            navigate(
                              `/orders/${orderId}/functions/${fn?.id}/bookings`
                            )
                          }
                        >
                          View Bookings
                        </Button>
                      </div>
                    ) : (
                      <div className="od-meta-row">
                        <Button
                          type="link"
                          className="od-bookings-link"
                          onClick={() =>
                            navigate(
                              `/orders/${orderId}/functions/${fn?.id}/bookings`
                            )
                          }
                        >
                          View Bookings <RightOutlined />
                        </Button>
                      </div>
                    )}

                    {/* VENUE */}
                    <div className="od-section">
                      <div className="od-section-title">
                        <EnvironmentOutlined /> Venue
                      </div>

                      {fn?.venue ? (
                        <div className="od-venue">
                          <div className="od-venue-media">
                            <div className="od-venue-img">
                              <Image
                                src={getImageUrl(fn?.venue?.image)}
                                alt={fn?.venue?.name}
                              />
                            </div>

                            {(fn?.venue?.files?.length || 0) > 0 && (
                              <div className="od-venue-thumbs">
                                <Image.PreviewGroup>
                                  {fn.venue.files.slice(0, 4).map((file) => (
                                    <div
                                      className="od-venue-thumb"
                                      key={file?.id}
                                    >
                                      <Image
                                        src={getImageUrl(file?.url)}
                                        alt={fn?.venue?.name}
                                      />
                                    </div>
                                  ))}
                                </Image.PreviewGroup>
                              </div>
                            )}
                          </div>

                          <div className="od-venue-info">
                            <h4>{fn?.venue?.name}</h4>

                            {(fn?.venue?.city?.name ||
                              fn?.venue?.area?.name) && (
                              <span className="od-chip od-venue-loc">
                                <EnvironmentOutlined />{" "}
                                {[
                                  fn?.venue?.area?.name,
                                  fn?.venue?.city?.name,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}

                            {/* FIX: strip HTML tags from description */}
                            {getPlainText(fn?.venue?.description) && (
                              <p>
                                {getPlainText(fn?.venue?.description)}
                              </p>
                            )}
                          </div>

                          <div className="od-venue-side">
                            <span className="od-venue-price">
                              {formatCurrency(fn?.venue?.amount || 0)}
                            </span>
                            {isEditing && (
                              <Popconfirm
                                title="Remove venue?"
                                okText="Remove"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => handleRemoveVenue(fi)}
                              >
                                <Button
                                  danger
                                  type="text"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                >
                                  Remove
                                </Button>
                              </Popconfirm>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="od-placeholder">
                          No venue added for this function.
                        </div>
                      )}
                    </div>

                    {/* ESSENTIALS */}
                    <div className="od-section">
                      <div className="od-section-title">
                        <ShoppingOutlined /> Essentials
                      </div>

                      {(fn?.essentials?.length || 0) === 0 ? (
                        <div className="od-placeholder">
                          No essentials added for this function.
                        </div>
                      ) : (
                        fn.essentials.map((ess, ei) => (
                          <div
                            className="od-essential"
                            key={
                              ess?.id || ess?.essential?.id || `ess-${ei}`
                            }
                          >
                            <div className="od-essential-head">
                              <h4 className="od-essential-name">
                                {ess?.essential?.name || "Essential"}
                              </h4>
                              <div className="od-essential-right">
                                <span className="od-essential-total">
                                  {formatCurrency(
                                    ess?.total_essential_amount || 0
                                  )}
                                </span>
                                {isEditing && (
                                  <Popconfirm
                                    title="Remove this essential?"
                                    okText="Remove"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() =>
                                      handleRemoveEssential(fi, ei)
                                    }
                                  >
                                    <Button
                                      danger
                                      type="text"
                                      size="small"
                                      icon={<DeleteOutlined />}
                                    />
                                  </Popconfirm>
                                )}
                              </div>
                            </div>

                            <div className="od-type-grid">
                              {(ess?.essential_types || []).map(
                                (type, ti) => (
                                  <div
                                    className="od-type-card"
                                    key={
                                      type?.id ||
                                      type?.essential_type?.id ||
                                      `type-${ti}`
                                    }
                                  >
                                    <div className="od-type-img">
                                      <Image
                                        src={getImageUrl(
                                          type?.essential_type
                                            ?.feature_image
                                        )}
                                        alt={type?.essential_type?.name}
                                      />
                                    </div>
                                    <div className="od-type-info">
                                      <span className="od-type-name">
                                        {type?.essential_type?.name ||
                                          "Item"}
                                      </span>
                                      <span className="od-type-price">
                                        {formatCurrency(type?.amount || 0)}
                                      </span>
                                    </div>
                                    {isEditing && (
                                      <Popconfirm
                                        title="Remove item?"
                                        okText="Remove"
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() =>
                                          handleRemoveEssentialType(
                                            fi,
                                            ei,
                                            ti
                                          )
                                        }
                                      >
                                        <Button
                                          className="od-type-remove"
                                          danger
                                          type="text"
                                          size="small"
                                          icon={<DeleteOutlined />}
                                        />
                                      </Popconfirm>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </section>
              ))
            )}
          </div>

          {/* ===== SIDEBAR ===== */}
          <aside className="od-side">
            {/* Customer */}
            <div className="od-card od-customer">
              <div className="od-card-title">
                <UserOutlined /> Customer
              </div>
              <div className="od-customer-row">
                <Avatar size={44} className="od-avatar">
                  {(orderDetails?.user?.name || "U").charAt(0).toUpperCase()}
                </Avatar>
                <div>
                  <div className="od-customer-name">
                    {orderDetails?.user?.name || "-"}
                  </div>
                  <div className="od-customer-sub">Customer</div>
                </div>
              </div>
              <div className="od-contact">
                <a
                  className="od-contact-item"
                  href={`tel:${orderDetails?.user?.mobile || ""}`}
                >
                  <PhoneOutlined /> {orderDetails?.user?.mobile || "-"}
                </a>
                <a
                  className="od-contact-item"
                  href={`mailto:${orderDetails?.user?.email || ""}`}
                >
                  <MailOutlined />
                  <span className="od-email">
                    {orderDetails?.user?.email || "-"}
                  </span>
                </a>
              </div>
            </div>

            {/* Price Summary */}
            <div className="od-card od-summary">
              <div className="od-card-title">
                <FileTextOutlined /> Price Summary
              </div>
              <div className="od-summary-sub">
                {counts.functions} Function
                {counts.functions !== 1 ? "s" : ""} • {counts.essentials}{" "}
                Essential{counts.essentials !== 1 ? "s" : ""} •{" "}
                {counts.items} Item{counts.items !== 1 ? "s" : ""}
              </div>

              <div className="od-sum-row">
                <span>Subtotal</span>
                <strong>
                  {formatCurrency(calculateTotalAmount(manipulatedData))}
                </strong>
              </div>

              <div className="od-sum-row">
                <span>Discount</span>
                {isEditing ? (
                  <InputNumber
                    min={0}
                    addonBefore="₹"
                    style={{ width: 130 }}
                    value={discount}
                    onChange={handleDiscountChange}
                  />
                ) : (
                  <strong className="od-discount">
                    - {formatCurrency(discount || 0)}
                  </strong>
                )}
              </div>

              <Divider style={{ margin: "12px 0" }} />

              <div className="od-final">
                <span>Final Amount</span>
                <strong>{formatCurrency(updateFinalAmount())}</strong>
              </div>

              {isEditing ? (
                <div className="od-remark">
                  <label>Remark</label>
                  <Input.TextArea
                    rows={3}
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="Add a note for this order..."
                  />
                </div>
              ) : (
                remark && (
                  <div className="od-remark-view">
                    <span className="od-remark-label">Remark</span>
                    <p>{remark}</p>
                  </div>
                )
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* ===== FIXED FOOTER (edit mode only) ===== */}
      {isEditing && (
        <div className="od-footer">
          <div className="od-footer-inner">
            <div className="od-footer-amount">
              <span>Final Amount</span>
              <strong>{formatCurrency(updateFinalAmount())}</strong>
            </div>
            <div className="od-footer-actions">
              <Button icon={<CloseOutlined />} onClick={handleCancel}>
                Discard
              </Button>
              <Button
                type="primary"
                className="od-btn-primary"
                icon={<CheckOutlined />}
                onClick={handleSubmit}
                loading={loading}
                disabled={loading}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ADD FUNCTION MODAL ===== */}
      {functionModal && orderDetails?.id && (
        <FunctionModal
          isShow={functionModal}
          hideModal={() => setFunctionModal(false)}
          orderDetails={orderDetails}
        />
      )}
    </div>
  );
};

export default OrderDetails;